import React, { useCallback, useRef, useState } from 'react';
import Chip from '../../shared/Chip';
import ListTable from '../../shared/ListTable';
import { LibraryRowMenu } from '../../Library/LibraryDocumentMenu';
import { useSettingsState } from '../../../../viewModels/useSettingsModel';
import UserDialog from './UserDialog';
import RemoveUserDialog from './RemoveUserDialog';

/**
 * Settings > Users (Navy Phase 0, story 30): the organization's users on the
 * List table, Name A to Z, each with Edit and Remove from org. Users are added
 * and removed here; Permissions sets what their roles can do. Editing a user
 * isn't built yet: Edit says so, as the prototype's does.
 */
function UsersSection({ model, onShowToast }) {
  const { users, userSort } = useSettingsState(model);
  const rootRef = useRef(null);
  const addRef = useRef(null);
  const [adding, setAdding] = useState(null); // { opener } while Add user is open
  const [removing, setRemoving] = useState(null); // { user, opener } while remove asks

  // The row's trigger, so focus goes back to it when a dialog closes.
  const menuTrigger = (user) => rootRef.current?.querySelector(`[data-user-menu="${user.id}"] button`) || null;

  const save = (fields) => {
    if (!model.addUser(fields)) return;
    setAdding(null);
    onShowToast?.('User added.', 'success');
  };

  const remove = () => {
    const { user } = removing;
    setRemoving(null);
    if (model.removeUser(user.id)) onShowToast?.('User removed.', 'success');
  };

  const closeAdding = useCallback(() => setAdding(null), []);

  const columns = [
    {
      key: 'name',
      title: 'Name',
      width: '200px',
      sort: 'text',
      value: (u) => u.name,
      render: (u) => <span className="library-cell library-cell--strong list-table-cut">{u.name}</span>,
    },
    {
      key: 'email',
      title: 'Email',
      width: 'minmax(200px, 1.4fr)',
      sort: 'text',
      value: (u) => u.email,
      render: (u) => <span className="library-cell list-table-cut">{u.email}</span>,
    },
    {
      key: 'role',
      title: 'Role',
      width: 'minmax(160px, 1fr)',
      sort: 'text',
      value: (u) => u.role,
      render: (u) => <span className="library-cell list-table-cut">{u.role}</span>,
    },
    {
      key: 'status',
      title: 'Status',
      width: '120px',
      sort: 'text',
      value: (u) => u.status,
      render: (u) => <Chip variant={u.status === 'Active' ? 'success' : 'standard'}>{u.status}</Chip>,
    },
    {
      key: 'actions',
      title: 'Actions',
      width: '80px',
      align: 'right',
      cellClassName: 'library-cell--menu',
      render: (u) => (
        <LibraryRowMenu
          item={u}
          anchorProps={{ 'data-user-menu': u.id }}
          actions={[
            { key: 'edit', label: 'Edit', icon: 'pencil-simple', run: () => onShowToast?.('Editing a user is not wired up in this prototype.', 'warn') },
            { key: 'remove', label: 'Remove from org', icon: 'trash-simple', danger: true, run: (user) => setRemoving({ user, opener: menuTrigger(user) }) },
          ]}
        />
      ),
    },
  ];

  return (
    <section className="navy-settings-section" ref={rootRef} aria-labelledby="navy-users-title">
      <div className="navy-settings-head">
        <h2 className="navy-settings-title" id="navy-users-title">Organization users</h2>
        <button ref={addRef} type="button" className="resui-btn resui-btn--primary" onClick={(e) => setAdding({ opener: e.currentTarget })}>
          Add user
        </button>
      </div>
      <div className="library-panel">
        <ListTable
          label="Organization users"
          columns={columns}
          rows={users}
          rowKey={(u) => u.id}
          sort={userSort}
          onSortChange={(sort) => model.setUserSort(sort)}
        />
      </div>

      {adding && (
        <UserDialog opener={adding.opener} fallbackFocus={addRef} onSave={save} onClose={closeAdding} />
      )}
      {removing && (
        <RemoveUserDialog user={removing.user} opener={removing.opener} fallbackFocus={addRef} onConfirm={remove} onClose={() => setRemoving(null)} />
      )}
    </section>
  );
}

export default UsersSection;
